import { ApplicationElement, html } from '../application_element/element.js';
import CommonActions from '../common_actions/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Base component for wrappers like: OrganizeOptionWrapper, DateOptionWrapper, PollOptionWrapper
 *
 * @prop totalCount - Total number of distinct users that have any option checked. It will be updated by component when user checks/unchecks.
 * @prop resultsUrl - When provided wraps 'n members assigned/voted' label to a link (optional)
 * @prop clearVotesUrl - API url that will remove all current user's votes on this card. Expected endpoint method: `delete`.
 * @prop initialClearOn - Should the "Clear votes" button be shown initially? Used when lazy loading options. Pass `true` only if you are sure the user has voted. (optional)
 * @prop isClosed - When true, disables all options and renders closed voting message (optional)
 * @prop resultsHidden - Should results be hidden on all the options? (optional)
 * @prop isMultipleChoice - Internal property - will be set automatically - can 2 options be selected at the same time?
 *
 * @cssprop [--notes-padding: 16px 0 16px 0] Padding of notes section
 * @cssprop [--notes-color: var(--dark-text-secondary)] Color of notes section text
 * @cssprop [--notes-font-size: var(--font-size-sm, 12px)] Size of notes section text
 * @cssprop [--notes-font-style: italic] Style of notes section text
 * @cssprop [--notes-gap: 4px] Gap between notes in notes section
 * @cssprop [--checkbox-button-border-radius: 4px] Border radius of multi-choice option buttons.
 * @cssprop [--radio-button-border-radius: 50%] Border radius of single-choice option buttons.
 *
 * @slot default - For card options
 * @slot clear - For clear votes action in footer
 * @slot actions - For additional actions in footer
 * @slot notes - For additional notes in left part of the footer
 */

export default class CardOptionWrapper extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    resultsUrl: { type: String },
    clearVotesUrl: { type: String },
    isClosed: { type: Boolean, reflect: true },
    totalCount: { type: Number, reflect: true },
    resultsHidden: { type: Boolean, reflect: true },
    initialClearOn: { type: Boolean },
    isMultipleChoice: { type: Boolean, reflect: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.totalCount = 0;

    /** @internal */
    this.commonActions = new CommonActions(this);
  }

  /** @internal */
  get options() {
    // implement options getter
    return Array.from(this.querySelectorAll('ef-card-option'));
  }

  /** @internal */
  get checkedCount() {
    return this.options.filter(o => o.checked).length;
  }

  /** @internal */
  get hideClearButton() {
    return (
      !this.initialClearOn &&
      (this.isClosed || !this.checkedCount || !this.clearVotesUrl)
    );
  }

  handleSlotChange() {
    let uniqueCount = 0;

    this.options.forEach(o => {
      o.resultsHidden ||= this.resultsHidden;
      o.disabled ||= this.isClosed;
      o.AppConfig = this.AppConfig;
      o.unique && uniqueCount++;
    });

    // if we have 2+ non-unique options - it is a multipleChoice
    this.isMultipleChoice =
      !uniqueCount || this.options.length - uniqueCount > 1;
    this.handleRadioUncheckPrevention();

    // When lazy loading options, handleSlotChange gets called twice:
    // once with skeletons (checkedCount = 0), once with options (checkedCount > 0).
    if (this.checkedCount) this.initialClearOn = false;
  }

  handleInput(e) {
    const { target } = e;
    const checked = target.checked;

    if (checked && this.checkedCount === 1) this.totalCount++;
    else if (!checked && this.checkedCount === 0) this.totalCount--;

    if (checked) this.handleRadioOptions(target);
  }

  handleRadioUncheckPrevention() {
    if (this.isMultipleChoice) return;

    this.options.forEach(o => {
      if (o.checked) o.preventInput = true;
      else o.preventInput = false;
    });
  }

  // We need a deferred version because ajax:complete is dispatched before the
  // options handleClick `finally` block is reached
  // If the function is not deferred, `finally` will overwrite preventInput
  deferredHandleRadioUncheckPrevention() {
    return setTimeout(() => this.handleRadioUncheckPrevention());
  }

  handleRadioOptions(option) {
    if (option.unique)
      // uncheck all other options
      this.options.forEach(o => o.itemId !== option.itemId && o.calcUncheck());
    // uncheck all other, unique options
    else this.options.forEach(o => o.unique && o.calcUncheck());
  }

  async handleClearVotes() {
    try {
      await this.commonActions.delete(this.clearVotesUrl);
      this.options.forEach(o => {
        if (o.checked) {
          o.calcUncheck();
          o.dispatchChangeEvent();
        }
      });
      this.handleRadioUncheckPrevention();
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  renderTotalLabel() {
    const label =
      this.isClosed && !this.totalCount
        ? this.t('no_total')
        : this.t('total', { count: this.totalCount });

    return this.resultsUrl
      ? html`<a href=${this.resultsUrl}>${label}</a>`
      : html`<div>${label}</div>`;
  }

  renderClosedLabel() {
    return html`<div>${this.t('closed')}</div>`;
  }

  render() {
    return html`
      <slot
        @ajax:complete=${this.deferredHandleRadioUncheckPrevention}
        @cardOption-change=${this.handleInput}
        @slotchange=${this.handleSlotChange}>
      </slot>
      <div class="note">
        <div class="labels">
          ${this.renderTotalLabel()}
          ${this.isClosed ? this.renderClosedLabel() : null}
          <slot name="notes"></slot>
        </div>
        <div class="buttons">
          <slot
            name="clear"
            ?hidden=${this.hideClearButton}
            @click=${this.handleClearVotes}>
            <button class="clear">${this.t('clear_votes')}</button>
          </slot>
          <slot name="actions"></slot>
        </div>
      </div>
    `;
  }
}

CardOptionWrapper.registerTagName('ef-card-option-wrapper');

customElements.get('ef-card-option-wrapper') ||
  customElements.define('ef-card-option-wrapper', CardOptionWrapper);
